import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import StudioNudge from "@/components/StudioNudge";
import { Download } from "lucide-react";
import { toast } from "sonner";

const Tax = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);

  const load = async (y) => {
    const r = await api.get(`/tax/summary?year=${y}`);
    setData(r.data);
  };
  useEffect(() => { load(year); }, [year]);

  const exportCSV = async () => {
    if (!data) return;
    const [invR, payR, expR] = await Promise.all([
      api.get("/invoices"),
      api.get("/payouts"),
      api.get(`/expenses?year=${year}`),
    ]);
    const rows = [["type", "date", "source", "amount", "currency", "status_or_category"]];
    invR.data.forEach((i) => rows.push(["invoice", i.paid_at || i.created_at, i.client_name, i.total, i.currency, i.status]));
    payR.data.forEach((p) => rows.push(["payout", p.created_at, p.rail, -p.amount, p.currency, p.status]));
    (expR.data || []).forEach((e) => rows.push(["expense", e.date, e.vendor, -Number(e.amount || 0), e.currency, e.category || "other"]));
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `quill-${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  if (!data) return <div className="p-10 text-zinc-500 text-sm">Loading…</div>;

  const years = [currentYear, currentYear - 1, currentYear - 2];

  return (
    <div data-testid="tax-page">
      <PageHeader
        overline="Compliance"
        title="Tax summary"
        description="Estimated income and tax liability across all your revenue streams."
        actions={
          <div className="flex items-center gap-2">
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}
              data-testid="year-select"
              className="bg-[#0A0A0A] border border-[#2c241e] px-3 py-2 text-sm focus:outline-none focus:border-white">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={exportCSV} data-testid="export-csv-btn"
              className="inline-flex items-center gap-2 bg-white text-black text-sm font-medium px-4 py-2 rounded-md hover:bg-zinc-200">
              <Download size={14} strokeWidth={2.5} /> Export CSV
            </button>
          </div>
        }
      />

      <StudioNudge variant="tax" />

      <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-[#2c241e]">
        <StatCard label="Gross income" value={fmtMoney(data.gross_total)} sub={`Year ${data.year}`} testId="tax-gross" />
        <StatCard label="Deductions" value={fmtMoney(data.deductible_expenses || 0)} sub="Schedule C" accent="success" testId="tax-deductions" />
        <StatCard label="Est. income tax" value={fmtMoney(data.estimated_tax_25pct)} sub="25% on net" accent="warning" testId="tax-income" />
        <StatCard label="Est. self-employment" value={fmtMoney(data.estimated_se_tax_15_3pct)} sub="15.3% SE tax" accent="warning" testId="tax-se" />
      </div>

      <div className="px-4 md:px-8 pt-4 md:pt-6">
        <div className="border border-[#2c241e] p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Net taxable income</div>
            <div className="mt-1 font-mono text-2xl md:text-3xl text-white" data-testid="tax-net-taxable">
              {fmtMoney(data.net_taxable_income ?? data.gross_total)}
            </div>
            <div className="text-xs text-zinc-500 mt-1">Gross income minus deductible expenses.</div>
          </div>
          <a
            href="/expenses"
            data-testid="tax-add-expense-cta"
            className="inline-flex items-center justify-center text-sm font-medium px-4 py-2 rounded-md hover:brightness-95"
            style={{
              background: "linear-gradient(180deg, hsl(var(--quill-info)) 0%, hsl(var(--quill-warm)) 100%)",
              color: "hsl(var(--quill-ink-warm))",
            }}
          >
            Track expenses
          </a>
        </div>
      </div>

      <div className="p-4 md:p-8">
        <div className="text-xs text-zinc-500 max-w-2xl">
          Estimates are for guidance only — flat 25% income tax and 15.3% US self-employment tax applied
          to net taxable income (gross minus deductible expenses). Consult a tax professional for advice
          specific to your jurisdiction.
        </div>
      </div>
    </div>
  );
};

export default Tax;
